import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCalendarPlus, FaChevronRight } from 'react-icons/fa6';
import { FaFileExport } from 'react-icons/fa';
import { FiCalendar } from 'react-icons/fi';
import _ from 'lodash';
import { format } from 'date-fns';
import { enGB, nl } from 'date-fns/locale';
import classNames from 'classnames';
import useEvents from '../../../hooks/community/useEvents';
import { Event, EventParticipationAnswer, EventViewType } from '../../../types/event';
import eventService from '../../../services/community/eventService';
import SearchBar from '../../components/forms/SearchBar';
import { formatDateAndTime } from '../../../utils/stringUtils';
import { filterBySearch } from '../../../utils/filterUtils';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import { usePagination } from '../../../hooks/effects/usePagination';
import DatePicker from '../../components/forms/DatePicker';
import dateUtils from '../../../utils/dateUtils';
import Button from '../../components/buttons/Button';
import AddToCalendarModal from '../../components/modals/AddToCalendarModal';
import ImportEventsModal from '../../components/modals/ImportEventsModal';
import useLanguage from '../../../hooks/account/useLanguage';
import Language from '../../../types/language';

export default function EventsOverviewTab(): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>('');
  const community = useSelectedCommunity();
  const allEvents = useEvents(EventViewType.ALL);
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(
    new Date(Date.now() + 1000 * 60 * 60 * 24 * 30 * 3),
  );
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const regularEvents = useMemo(
    () => allEvents.filter((event) => event.scheduleId === null),
    [allEvents],
  );

  const filteredEvents = useMemo(() => {
    return filterBySearch(regularEvents, search, 'title', 'description').filter((e) =>
      dateUtils.isInBounds(e.startTime, startDate, endDate),
    );
  }, [regularEvents, search, startDate, endDate]);

  const sortedEvents = useMemo(
    () => [...filteredEvents].sort((a, b) => a.startTime.getTime() - b.startTime.getTime()),
    [filteredEvents],
  );

  const { items: eventsPerPage, ...pagination } = usePagination(sortedEvents);

  useEffect(() => {
    pagination.setPerPage(20);
  }, []);

  const byMonth = useMemo(
    () => _.groupBy(eventsPerPage, (event) => format(event.startTime, 'yyyy-MM')),
    [eventsPerPage],
  );

  useEffect(() => {
    eventService.getUpcomingEvents().then(() => eventService.prependPastEvents());
  }, [community]);

  useEffect(() => {
    pagination.setPage(1);
  }, [startDate, endDate, search]);

  return (
    <div className="flex flex-1 flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary-outline"
            onClick={() => setIsCalendarModalOpen(true)}
            className="flex items-center gap-2 whitespace-nowrap">
            <FaFileExport className="h-4 w-4" />
            {t('page.event.calendar.add')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setIsImportModalOpen(true)}
            className="flex items-center gap-2 whitespace-nowrap">
            <FaCalendarPlus className="h-4 w-4" />
            {t('page.event.addEvent.import')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <SearchBar
          inputProps={{ placeholder: t('page.event.regular.searchEvents') }}
          className="max-w-[380px]"
          search={search}
          setSearch={setSearch}
        />
        {/* Single visual date-range chip (two bound pickers, one control look) */}
        <div className="flex items-center gap-2 rounded-[8px] border border-secondary-200 bg-secondary-200/70 px-3 py-1.5">
          <FiCalendar className="h-4 w-4 flex-shrink-0 text-primary-900" />
          <DatePicker
            customFormat="D MMM YYYY"
            date={startDate}
            setDate={setStartDate}
            includeTime={false}
            className="!border-0 !bg-transparent !p-0 !shadow-none"
          />
          <span className="text-gray-400">–</span>
          <DatePicker
            customFormat="D MMM YYYY"
            date={endDate}
            setDate={setEndDate}
            includeTime={false}
            className="!border-0 !bg-transparent !p-0 !shadow-none"
          />
        </div>
      </div>

      {regularEvents.length === 0 ? (
        <EmptyScreenView subtitle={t('page.event.regular.noEventsFound')} />
      ) : filteredEvents.length === 0 ? (
        <EmptyScreenView
          containerClassName="max-w-96 mx-auto gap-1"
          subtitle={t('page.event.regular.noEventsFound')}
        />
      ) : (
        <div className="flex flex-col gap-6">
          {Object.entries(byMonth).map(([monthKey, monthEvents]) => (
            <MonthGroup key={monthKey} monthKey={monthKey} events={monthEvents} />
          ))}
          {pagination.numberOfPages > 1 && (
            <div className="flex items-center justify-end gap-2 text-sm text-gray-500">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => pagination.setPage(pagination.page - 1)}
                className="rounded-[8px] px-2 py-1 hover:bg-secondary-200 disabled:opacity-40">
                ‹
              </button>
              <span>
                {t('component.pagination.pageOf', {
                  defaultValue: 'Page {{page}} of {{total}}',
                  page: pagination.page,
                  total: pagination.numberOfPages,
                })}
              </span>
              <button
                type="button"
                disabled={pagination.page >= pagination.numberOfPages}
                onClick={() => pagination.setPage(pagination.page + 1)}
                className="rounded-[8px] px-2 py-1 hover:bg-secondary-200 disabled:opacity-40">
                ›
              </button>
            </div>
          )}
        </div>
      )}

      <AddToCalendarModal open={isCalendarModalOpen} setOpen={setIsCalendarModalOpen} />
      <ImportEventsModal open={isImportModalOpen} setOpen={setIsImportModalOpen} />
    </div>
  );
}

function MonthGroup({ monthKey, events }: { monthKey: string; events: Event[] }) {
  const lang = useLanguage();
  const locale = lang === Language.NL ? nl : enGB;
  const label = format(new Date(`${monthKey}-01`), 'MMM yyyy', { locale }).toUpperCase();

  return (
    <div className="flex flex-col gap-2">
      <p className="px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      <div className="flex flex-col gap-2">
        {events.map((event) => (
          <EventTile key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}

function EventTile({ event }: { event: Event }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useLanguage();
  const locale = lang === Language.NL ? nl : enGB;
  const counts = _.countBy(event.participants, 'answer');
  const yes = counts[EventParticipationAnswer.YES] ?? 0;
  const maybe = counts[EventParticipationAnswer.MAYBE] ?? 0;
  const no = counts[EventParticipationAnswer.NO] ?? 0;
  const pending = counts[EventParticipationAnswer.PENDING] ?? 0;
  const total = yes + maybe + no + pending || 1;

  return (
    <button
      type="button"
      onClick={() =>
        navigate('/events/details', { state: { eventId: event.id, origin: 'overview' } })
      }
      className="flex w-full items-center gap-3 rounded-[8px] border border-secondary-200 bg-secondary-200/60 px-3 py-3 text-left transition-colors hover:bg-secondary-200">
      <div className="flex h-12 w-12 flex-shrink-0 flex-col items-center justify-center rounded-[8px] border border-secondary-200 bg-surface">
        <span className="text-[10px] font-semibold uppercase text-gray-500">
          {format(event.startTime, 'EEE', { locale })}
        </span>
        <span className="text-lg font-semibold leading-none text-primary-900">
          {format(event.startTime, 'd')}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h4 className="truncate font-semibold text-primary-900">{event.title}</h4>
          {event.externalCalendarUrl && (
            <span className="text-xs text-gray-400">
              {t('page.event.regular.externalCalendar')}
            </span>
          )}
        </div>
        <p className="truncate text-sm text-gray-500">
          {[
            event.endTime
              ? `${format(event.startTime, 'HH:mm')} – ${format(event.endTime, 'HH:mm')}`
              : format(event.startTime, 'HH:mm'),
            event.location,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {event.attendanceRequired && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <div className="flex h-1.5 w-28 overflow-hidden rounded-full bg-gray-200">
              <div className="bg-green-500" style={{ width: `${(yes / total) * 100}%` }} />
              <div className="bg-orange-400" style={{ width: `${(maybe / total) * 100}%` }} />
              <div className="bg-red-500" style={{ width: `${(no / total) * 100}%` }} />
            </div>
            <span className="text-xs text-gray-500">
              {yes} yes · {maybe} maybe · {no} no
            </span>
            {pending > 0 && <span className="text-xs text-gray-400">{pending} not responded</span>}
          </div>
        )}
        {!event.attendanceRequired && event.priceOfTicket && event.priceOfTicket > 0 && (
          <p className="mt-1 text-xs text-gray-500">{formatDateAndTime(event.startTime)}</p>
        )}
      </div>

      <FaChevronRight className={classNames('h-4 w-4 flex-shrink-0 text-primary-300')} />
    </button>
  );
}
